import React from 'react';
import {
  Box,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  Checkbox,
  ListItemText,
} from '@mui/material';

type OptionType = { 
  value: string;
  label: string;
  color?: string;  // チェックボックスの色
  chipColor?: 'success' | 'info' | 'default' | 'warning' | 'primary' | 'error' | 'secondary';
  customStyle?: {  // カスタムスタイル
    backgroundColor: string;
    color: string;
    borderColor?: string;
  };
};

interface MultiStatusSelectProps {
  label: string;
  id: string;
  value: string[];
  onChange: (event: any) => void;
  options: Record<string, string> | OptionType[];
  emptyLabel?: string;
  fullWidth?: boolean;
  size?: 'small' | 'medium';
  disabled?: boolean;
  renderChips?: boolean;  // Chip表示するかどうか
}

const MultiStatusSelect: React.FC<MultiStatusSelectProps> = ({
  label,
  id,
  value,
  onChange,
  options,
  emptyLabel = 'すべて',
  fullWidth = true,
  size = 'small',
  disabled = false,
  renderChips = false,
}) => {
  // optionsが配列かオブジェクトかを判断
  const isOptionsArray = Array.isArray(options);
  
  // オプションの配列を生成
  const optionsArray = isOptionsArray 
    ? options as OptionType[]
    : Object.entries(options as Record<string, string>).map(([value, label]) => ({
        value,
        label,
        color: undefined,
        chipColor: undefined,
        customStyle: undefined
      }));

  // 選択されているオプションのラベルを結合
  const renderValue = (selected: string[]) => {
    if (selected.length === 0) return emptyLabel;
    
    if (renderChips) {
      return (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {selected.map(selectedValue => {
            const option = optionsArray.find(opt => opt.value === selectedValue);
            if (!option) return null;
            return (
              <Chip
                key={selectedValue}
                label={option.label}
                size="small"
                color={option.customStyle ? 'default' : (option.chipColor || 'default')}
                sx={{
                  ...(option.customStyle && {
                    backgroundColor: option.customStyle.backgroundColor,
                    color: option.customStyle.color,
                    borderColor: option.customStyle.borderColor || option.customStyle.backgroundColor,
                    '&:hover': {
                      backgroundColor: option.customStyle.backgroundColor,
                    }
                  })
                }}
              />
            );
          })}
        </Box>
      );
    }
    
    return selected
      .map(selectedValue => {
        const option = optionsArray.find(opt => opt.value === selectedValue);
        return option ? option.label : selectedValue;
      })
      .join(', ');
  };

  return (
    <FormControl fullWidth={fullWidth} size={size} disabled={disabled}>
      <InputLabel id={`${id}-label`}>{label}</InputLabel>
      <Select
        labelId={`${id}-label`}
        id={id}
        multiple
        value={value}
        onChange={onChange}
        input={<OutlinedInput label={label} />}
        renderValue={renderValue}
      >
        {optionsArray.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            <Checkbox 
              checked={value.indexOf(option.value) > -1} 
              style={option.color ? { color: option.color } : undefined}
            />
            <ListItemText 
              primary={option.label} 
              style={
                option.color && value.indexOf(option.value) > -1 
                  ? { color: option.color } 
                  : undefined
              }
            />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export default MultiStatusSelect;